import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBook, FaPlus, FaSearch, FaTimes, FaEdit } from 'react-icons/fa';
import { bookService } from '../../../services/book.service';
import { seedBooks } from '../../../utils/seedBooks';
import { useAuth } from '../../../context/AuthContext';
import { userService } from '../../../services/user.service';
import './BookLibrary.css';

const BookLibrary = () => {
  const [userLibrary, setUserLibrary] = useState([]);
  const [isAddingBook, setIsAddingBook] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [availableBooks, setAvailableBooks] = useState([]);
  const [editingBook, setEditingBook] = useState(null);
  const [progressInput, setProgressInput] = useState('');

  useEffect(() => {
    if (user) {
      loadUserLibrary();
      loadAvailableBooks();
    }
  }, [user]);

  const loadUserLibrary = async () => {
    try {
      const library = await bookService.getUserLibrary(user.uid);
      setUserLibrary(library);
    } catch (error) {
      console.error('Error loading library:', error);
    }
  };

  const loadAvailableBooks = async () => {
    try {
      const books = await bookService.getBookCatalog();
      setAvailableBooks(books);
    } catch (error) {
      console.error('Error loading books:', error);
    }
  };

  const handleSeedBooks = async () => {
    try {
      setIsSeeding(true);
      await seedBooks();
      await loadUserLibrary(); // Refresh the library after seeding
    } catch (error) {
      console.error('Error seeding books:', error);
    } finally {
      setIsSeeding(false);
    }
  };

  const handleSetAdmin = async () => {
    try {
      await userService.setAdminRole(user.uid);
      console.log('Admin role set successfully');
    } catch (error) {
      console.error('Error setting admin role:', error);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (!value.trim()) {
      setSearchResults([]);
      return;
    }

    const results = availableBooks.filter(book => 
      book.title.toLowerCase().includes(value.toLowerCase()) ||
      book.author.toLowerCase().includes(value.toLowerCase()) ||
      book.categories.some(category => 
        category.toLowerCase().includes(value.toLowerCase())
      )
    );
    setSearchResults(results);
  };

  const handleAddToLibrary = async (book) => {
    try {
      await bookService.addToLibrary(user.uid, book.id);
      await loadUserLibrary();
      setIsAddingBook(false);
      setSearchTerm('');
      setSearchResults([]);
    } catch (error) {
      console.error('Error adding book to library:', error);
    }
  };

  const handleUpdateProgress = async (book) => {
    try {
      const progress = Math.min(Math.max(0, parseInt(progressInput)), book.pages);
      await bookService.updateBookProgress(book.id, { progress });
      await loadUserLibrary();
      setEditingBook(null);
      setProgressInput('');
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const renderBookCard = (book) => (
    <motion.div 
      key={book.id}
      className="search-result-item"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="book-preview">
        <img src={book.coverUrl} alt={book.title} />
        <div className="book-preview-info">
          <h4>{book.title}</h4>
          <p>{book.author}</p>
          <div className="book-progress">
            <div 
              className="progress-bar"
              style={{ width: `${(book.progress / book.pages) * 100}%` }}
            />
            <div className="progress-info">
              <span className="progress-text">
                {book.progress} / {book.pages} pages
              </span>
              <button 
                className="edit-progress-button"
                onClick={() => {
                  setEditingBook(book);
                  setProgressInput(book.progress.toString());
                }}
              >
                <FaEdit />
              </button>
            </div>
          </div>
          {book.categories && (
            <p className="book-categories">
              {book.categories.join(' • ')}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );

  const renderSearchResults = () => (
    <div className="search-results">
      {searchResults.map(book => (
        <div key={book.id} className="search-result-item">
          <div className="book-preview">
            <img src={book.coverUrl} alt={book.title} />
            <div className="book-preview-info">
              <h4>{book.title}</h4>
              <p>{book.author}</p>
              <p className="book-categories">
                {book.categories.join(' • ')}
              </p>
            </div>
          </div>
          <button 
            className="add-to-library-button"
            onClick={() => handleAddToLibrary(book)}
          >
            Add to Library
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <div className="book-library">
      <div className="library-header">
        <div className="library-header-left">
          <h2>My Books</h2>
          <button 
            className="seed-button"
            onClick={handleSeedBooks}
            disabled={isSeeding}
          >
            {isSeeding ? 'Seeding...' : 'Seed Books'}
          </button>
          <button 
            className="seed-button"
            onClick={handleSetAdmin}
          >
            Set Admin
          </button>
        </div>
        <button 
          className="add-book-button"
          onClick={() => setIsAddingBook(true)}
        >
          <FaPlus />
          Add Book
        </button>
      </div>

      <div className="library-content">
        {userLibrary.length === 0 ? (
          <div className="empty-library">
            <FaBook />
            <p>No books in your library yet</p>
          </div>
        ) : (
          <div className="books-grid">
            {userLibrary.map(renderBookCard)}
          </div>
        )}
      </div>

      <AnimatePresence>
        {isAddingBook && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsAddingBook(false)}
          >
            <motion.div 
              className="add-book-modal"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Add Book to Library</h3>
                <button 
                  className="close-button"
                  onClick={() => setIsAddingBook(false)}
                >
                  <FaTimes />
                </button>
              </div>
              
              <div className="search-container">
                <div className="search-input-wrapper">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search by title, author, or category..."
                    className="search-input"
                    autoFocus
                  />
                </div>
              </div>

              {searchTerm && renderSearchResults()}
            </motion.div>
          </motion.div>
        )}

        {editingBook && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setEditingBook(null)}
          >
            <motion.div 
              className="edit-progress-modal"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Update Progress</h3>
                <button 
                  className="close-button"
                  onClick={() => setEditingBook(null)}
                >
                  <FaTimes />
                </button>
              </div>
              
              <div className="progress-input-container">
                <input
                  type="number"
                  value={progressInput}
                  onChange={(e) => setProgressInput(e.target.value)}
                  min="0"
                  max={editingBook.pages}
                  className="progress-input"
                  autoFocus
                />
                <span className="progress-total">/ {editingBook.pages} pages</span>
              </div>

              <button 
                className="update-progress-button"
                onClick={() => handleUpdateProgress(editingBook)}
              >
                Update Progress
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BookLibrary; 